import {
	createFileRoute,
	Link,
	Navigate,
	useNavigate,
	useRouter,
} from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import {
	commitLocalUpdate,
	graphql,
	useFragment,
	useLazyLoadQuery,
	useRelayEnvironment,
} from "react-relay";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Image } from "@/components/image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { logout as logoutHandler } from "@/handlers/logout";
import type { clubs_club$key } from "./__generated__/clubs_club.graphql";
import type { clubsQuery } from "./__generated__/clubsQuery.graphql";

export const Route = createFileRoute("/clubs")({
	component: RouteComponent,
	head: () => {
		return {
			meta: [
				{
					name: "robots",
					content: "noindex,nofollow",
				},
			],
		};
	},
});

function ClubCard({ club }: { club: clubs_club$key }) {
	const [isHovering, setIsHovering] = useState(false);
	const data = useFragment<clubs_club$key>(
		graphql`
    fragment clubs_club on Club {
        id
        name
        slug
        logo {
          signedUrl
        }
        domains {
          enabled
          domain
        }
        onboardingCompleted: configuration(key: "ONBOARDING_COMPLETED")
        onboardingLastPage: configuration(key: "ONBOARDING_LAST_PAGE")
    }
  `,
		club,
	);

	const getLastPage = ():
		| "/onboarding/$club/theme"
		| "/onboarding/$club/feeds"
		| "/onboarding/$club/cursos"
		| "/onboarding/$club/ranking"
		| "/onboarding/$club/eventos"
		| "/onboarding/$club/planos"
		| "/onboarding/$club/features"
		| "/onboarding/$club/launch" => {
		switch (data.onboardingLastPage) {
			case "courses":
				return "/onboarding/$club/feeds";
			case "features":
				return "/onboarding/$club/cursos";
			case "feeds":
				return "/onboarding/$club/eventos";
			case "events":
				return "/onboarding/$club/ranking";
			case "ranking":
				return "/onboarding/$club/planos";
			case "theme":
				return "/onboarding/$club/features";
			default:
				return "/onboarding/$club/theme";
		}
	};

	const destination =
		data.onboardingCompleted === "1" ? "/c/$club/analytics" : getLastPage();

	return (
		<Link
			key={data.id}
			to={destination}
			params={{ club: data.slug }}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			<div className="flex flex-row items-center justify-between gap-3 p-3 rounded-md border">
				<div className="flex flex-row items-center justify-start gap-3">
					<Avatar className="border">
						<AvatarImage src={data.logo?.signedUrl} />
						<AvatarFallback>{data.name.slice(0, 2)}</AvatarFallback>
					</Avatar>
					<div className="flex flex-col">
						<span className="font-medium text-base/6 line-clamp-1">
							{data.name}
						</span>

						<span className="text-xs/5 text-muted-foreground line-clamp-1">
							{data.domains.find((d) => d.enabled)?.domain || "-"}
						</span>
					</div>
				</div>

				<AnimatePresence>
					{isHovering && (
						<motion.div
							initial={{ x: 10, opacity: 0 }}
							animate={{ x: 0, opacity: 1 }}
							exit={{ x: 10, opacity: 0 }}
						>
							<Icon
								name="MoveUpRight"
								className="size-3 text-muted-foreground"
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</Link>
	);
}

function RouteComponent() {
	const logout = useServerFn(logoutHandler);
	const environment = useRelayEnvironment();
	const navigate = useNavigate({ from: Route.id });
	const router = useRouter();
	const data = useLazyLoadQuery<clubsQuery>(
		graphql`
    query clubsQuery {
        user {
          clubs {
            id
            ...clubs_club
          }
        }
    }
  `,
		{},
	);

	if (!data.user?.clubs.length) {
		return <Navigate to="/onboarding" />;
	}

	return (
		<div className="size-full overflow-hidden max-h-100dvh">
			<div className="h-16 border border-sidebar-border px-6 bg-sidebar text-sidebar-foreground grid gap-6 grid-cols-2 w-full">
				<div className="flex items-center justify-start">
					<Image
						src="https://storage.googleapis.com/scarf-club-assets/logos/Scarf-Full-Primary.svg"
						className="h-7 w-fit"
						height={240}
					/>
				</div>

				<div className="flex items-center justify-end">
					<DropdownMenu>
						<DropdownMenuTrigger>
							<Avatar className="border cursor-pointer">
								<AvatarFallback>TH</AvatarFallback>
							</Avatar>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onClick={async () => {
									if (confirm("Tem certeza que deseja fazer logout?")) {
										logout().then(async () => {
											commitLocalUpdate(environment, (store) =>
												store.invalidateStore(),
											);

											router.invalidate();

											navigate({
												reloadDocument: true,
											});
										});
									}
								}}
							>
								<Icon name="LogOut" className="size-4" />
								Logout
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>
			<div className="flex items-center justify-start p-6 lg:py-16 xl:py-24 flex-col gap-6 md:overflow-y-auto">
				<div className="flex flex-col gap-3 items-center">
					<Heading className="lg:text-4xl/6">Seus Ecossistemas</Heading>

					<div className="text-muted-foreground text-sm/5">
						Selecione o ecossistema que deseja acessar
					</div>
				</div>

				<div className="w-full max-w-[1000px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{data.user?.clubs.map((c) => (
						<ClubCard key={c.id} club={c} />
					))}
				</div>
			</div>
		</div>
	);
}
